import {apiFetch} from '../../../../security/apiFetch';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Typography, Box, Button, Alert, Chip, Accordion, AccordionSummary, AccordionDetails, IconButton, TextField, LinearProgress, RadioGroup, FormControlLabel, Radio, FormControl } from '@mui/material';
import { VolumeUp, ExpandMore, Star, StarBorder, Quiz, CheckCircle, Cancel, Refresh } from '@mui/icons-material';
import { LessonStep } from '../../types';
import { playAudio } from '../../../../utils/audioUtils';

interface TermsStepProps {
  step: LessonStep;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
}

interface Term {
  id: string;
  term_arabic: string;
  term_transliteration: string;
  term_transliteration_cyr: string;
  definition_short: string;
  definition_full: string;
  category: string;
  audio_url?: string;
  is_favorite?: boolean;
  examples?: string[];
  related_terms?: string[];
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation: string;
  term_id: string;
}

type StudyMode = 'browse' | 'quiz' | 'practice';

interface StudyProgress {
  term_id: string;
  times_viewed: number;
  quiz_score: number;
  last_studied: string;
  is_mastered: boolean;
}

const TermsStep: React.FC<TermsStepProps> = ({ step, token, onNext, lessonId }) => {
  const [terms, setTerms] = useState<Term[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState<string | null>(null);
  const [favoriteTerms, setFavoriteTerms] = useState<Set<string>>(new Set());
  
  // Новые состояния для полноценного изучения
  const [studyMode, setStudyMode] = useState<StudyMode>('browse');
  const [currentTermIndex, setCurrentTermIndex] = useState(0);
  const [studyProgress, setStudyProgress] = useState<Map<string, StudyProgress>>(new Map());
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [practiceInput, setPracticeInput] = useState('');

  // Загружаем термины для урока
  useEffect(() => {
    const loadTerms = async () => {
      try {
        const response = await apiFetch(`/api/v1/arabic-lessons/lessons/${lessonId}/terms`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setTerms(data.terms || []);
          
          // Загружаем избранные термины
          const favoritesResponse = await apiFetch(`/api/v1/user-terms/favorites`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          
          if (favoritesResponse.ok) {
            const favoritesData = await favoritesResponse.json();
            const favoriteIds = new Set<string>(favoritesData.items?.map((item: any) => item.id) || []);
            setFavoriteTerms(favoriteIds);
          }
        } else {
          // Fallback к базовым терминам
          setTerms(getBasicTerms(step.content.letter || 'ا'));
        }
      } catch (error) {
        console.error('Ошибка загрузки терминов:', error);
        setTerms(getBasicTerms(step.content.letter || 'ا'));
      } finally {
        setIsLoading(false);
      }
    };

    loadTerms();
  }, [lessonId, token, step.content.letter]);

  const getBasicTerms = (letter: string): Term[] => {
    // Возвращаем пустой массив - термины должны загружаться из API
    return [];
  };

  // Генерация вопросов викторины
  const generateQuizQuestions = useCallback((terms: Term[]): QuizQuestion[] => {
    const questions: QuizQuestion[] = [];
    
    terms.forEach(term => {
      // Вопрос о значении термина
      questions.push({
        id: `meaning-${term.id}`,
        question: `Что означает термин "${term.term_arabic}"?`,
        options: [
          term.definition_short,
          ...terms.filter(t => t.id !== term.id).slice(0, 3).map(t => t.definition_short)
        ].sort(() => Math.random() - 0.5),
        correct_answer: 0,
        explanation: term.definition_full,
        term_id: term.id
      });

      // Вопрос о транскрипции
      questions.push({
        id: `transliteration-${term.id}`,
        question: `Как читается "${term.term_arabic}"?`,
        options: [
          term.term_transliteration,
          ...terms.filter(t => t.id !== term.id).slice(0, 3).map(t => t.term_transliteration)
        ].sort(() => Math.random() - 0.5),
        correct_answer: 0,
        explanation: `Правильная транскрипция: ${term.term_transliteration} (${term.term_transliteration_cyr})`,
        term_id: term.id
      });
    });

    return questions.sort(() => Math.random() - 0.5);
  }, []);

  // Обновление прогресса изучения
  const updateStudyProgress = useCallback((termId: string, action: 'view' | 'quiz' | 'master') => {
    setStudyProgress(prev => {
      const newProgress = new Map(prev);
      const current = newProgress.get(termId) || {
        term_id: termId,
        times_viewed: 0,
        quiz_score: 0,
        last_studied: new Date().toISOString(),
        is_mastered: false
      };

      switch (action) {
        case 'view':
          current.times_viewed += 1;
          break;
        case 'quiz':
          current.quiz_score += 1;
          break;
        case 'master':
          current.is_mastered = true;
          break;
      }

      current.last_studied = new Date().toISOString();
      newProgress.set(termId, current);
      return newProgress;
    });
  }, []);

  const handlePlayAudio = async (term: Term) => {
    if (!term.audio_url) return;
    
    try {
      setIsPlaying(term.id);
      await playAudio(term.audio_url, token);
    } catch (error) {
      console.error('Ошибка воспроизведения аудио:', error);
    } finally {
      setIsPlaying(null);
    }
  };

  const handleToggleFavorite = async (termId: string) => {
    try {
      const isFavorite = favoriteTerms.has(termId);
      const method = isFavorite ? 'DELETE' : 'POST';
      
      const response = await apiFetch(`/api/v1/user-terms/favorites/${termId}`, {
        method,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        setFavoriteTerms(prev => {
          const newSet = new Set(prev);
          if (isFavorite) {
            newSet.delete(termId);
          } else {
            newSet.add(termId);
          }
          return newSet;
        });
      }
    } catch (error) {
      console.error('Ошибка изменения избранного:', error);
    }
  };

  // Функции для управления викториной
  const startQuiz = useCallback(() => {
    const questions = generateQuizQuestions(terms);
    setQuizQuestions(questions);
    setCurrentQuizIndex(0);
    setQuizAnswers([]);
    setShowQuizResults(false);
    setStudyMode('quiz');
  }, [terms, generateQuizQuestions]);

  const handleQuizAnswer = useCallback((answerIndex: number) => {
    const newAnswers = [...quizAnswers, answerIndex];
    setQuizAnswers(newAnswers);
    
    if (currentQuizIndex < quizQuestions.length - 1) {
      setCurrentQuizIndex(currentQuizIndex + 1);
    } else {
      setShowQuizResults(true);
    }
  }, [quizAnswers, currentQuizIndex, quizQuestions.length]);

  const resetQuiz = useCallback(() => {
    setCurrentQuizIndex(0);
    setQuizAnswers([]);
    setShowQuizResults(false);
  }, []);

  // Функции для практики
  const startPractice = useCallback(() => {
    setCurrentTermIndex(0);
    setPracticeInput('');
    setStudyMode('practice');
  }, []);

  const handlePracticeInput = useCallback((input: string) => {
    setPracticeInput(input);
  }, []);

  const checkPracticeAnswer = useCallback((term: Term) => {
    const isCorrect = practiceInput.toLowerCase().trim() === term.term_transliteration.toLowerCase() ||
                     practiceInput.toLowerCase().trim() === term.term_transliteration_cyr.toLowerCase();
    
    if (isCorrect) {
      updateStudyProgress(term.id, 'master');
    }
    
    return isCorrect;
  }, [practiceInput, updateStudyProgress]);

  const nextTerm = useCallback(() => {
    if (currentTermIndex < terms.length - 1) {
      setCurrentTermIndex(currentTermIndex + 1);
      setPracticeInput('');
    }
  }, [currentTermIndex, terms.length]);

  const getCategoryColor = (category: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
      'tajweed': 'primary',
      'fiqh': 'secondary',
      'aqeedah': 'error',
      'grammar': 'info',
      'general': 'default'
    };
    return colors[category] || 'default';
  };

  const getCategoryLabel = (category: string): string => {
    const labels: { [key: string]: string } = {
      'tajweed': 'Таджвид',
      'fiqh': 'Фикх',
      'aqeedah': 'Акыда',
      'grammar': 'Грамматика',
      'general': 'Общее'
    };
    return labels[category] || category;
  };

  if (isLoading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {step.title}
          </Typography>
          <Alert severity="info">
            Загружаем термины...
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (terms.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {step.title}
          </Typography>
          <Alert severity="warning">
            Термины для этой буквы не найдены
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="contained" onClick={() => onNext()}>
              Продолжить
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {step.title}
        </Typography>
        
        {step.content.instruction && (
          <Typography variant="body1" sx={{ mb: 3 }}>
            {step.content.instruction}
          </Typography>
        )}

        {/* Переключатель режимов изучения */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Режим изучения:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant={studyMode === 'browse' ? 'contained' : 'outlined'}
              onClick={() => setStudyMode('browse')}
            >
              Просмотр
            </Button>
            <Button
              variant={studyMode === 'quiz' ? 'contained' : 'outlined'}
              onClick={startQuiz}
              startIcon={<Quiz />}
            >
              Викторина
            </Button>
            <Button
              variant={studyMode === 'practice' ? 'contained' : 'outlined'}
              onClick={startPractice}
            >
              Практика
            </Button>
          </Box>
        </Box>

        {/* Компоненты для разных режимов */}
        {studyMode === 'quiz' && (
          <Box sx={{ mb: 3 }}>
            {!showQuizResults ? (
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Викторина: Вопрос {currentQuizIndex + 1} из {quizQuestions.length}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={((currentQuizIndex + 1) / quizQuestions.length) * 100} 
                  sx={{ mb: 3 }}
                />
                
                {quizQuestions[currentQuizIndex] && (
                  <>
                    <Typography variant="h5" sx={{ mb: 3 }}>
                      {quizQuestions[currentQuizIndex].question}
                    </Typography>
                    
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={quizAnswers[currentQuizIndex] ?? ''}
                        onChange={(e) => handleQuizAnswer(parseInt(e.target.value))}
                      >
                        {quizQuestions[currentQuizIndex].options.map((option, index) => (
                          <FormControlLabel
                            key={index}
                            value={index}
                            control={<Radio />}
                            label={option}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </>
                )}
              </Card>
            ) : (
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Результаты викторины
                </Typography>
                
                {quizQuestions.map((question, index) => {
                  const isCorrect = quizAnswers[index] === question.correct_answer;
                  return (
                    <Box key={question.id} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {isCorrect ? <CheckCircle color="success" /> : <Cancel color="error" />}
                        <Typography variant="subtitle2">
                          {isCorrect ? 'Правильно' : 'Неправильно'}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {question.question}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        {question.explanation}
                      </Typography>
                    </Box>
                  );
                })}
                
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button variant="outlined" onClick={resetQuiz} startIcon={<Refresh />}>
                    Повторить
                  </Button>
                  <Button variant="contained" onClick={() => setStudyMode('browse')}>
                    Завершить
                  </Button>
                </Box>
              </Card>
            )}
          </Box>
        )}

        {/* Режим практики */}
        {studyMode === 'practice' && (
          <Box sx={{ mb: 3 }}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Практика: Термин {currentTermIndex + 1} из {terms.length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={((currentTermIndex + 1) / terms.length) * 100} 
                sx={{ mb: 3 }}
              />
              
              {terms[currentTermIndex] && (
                <>
                  <Typography variant="h4" sx={{ fontFamily: 'Noto Naskh Arabic, serif', mb: 2 }}>
                    {terms[currentTermIndex].term_arabic}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {terms[currentTermIndex].definition_short}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Введите транскрипцию"
                    value={practiceInput}
                    onChange={(e) => handlePracticeInput(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const isCorrect = checkPracticeAnswer(terms[currentTermIndex]);
                        if (isCorrect) {
                          nextTerm();
                        }
                      }}
                    >
                      Проверить
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={nextTerm}
                      disabled={currentTermIndex >= terms.length - 1}
                    >
                      Следующий
                    </Button>
                  </Box>
                </>
              )}
            </Card>
          </Box>
        )}

        {/* Список терминов */}
        <Box sx={{ mb: 3 }}>
          {terms.map((term, index) => (
            <Accordion key={term.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
                    {term.term_arabic}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={getCategoryLabel(term.category)} 
                      color={getCategoryColor(term.category)}
                      size="small"
                    />
                    
                    {term.audio_url && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayAudio(term);
                        }}
                        disabled={isPlaying === term.id}
                      >
                        <VolumeUp />
                      </IconButton>
                    )}
                    
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(term.id);
                      }}
                    >
                      {favoriteTerms.has(term.id) ? <Star color="warning" /> : <StarBorder />}
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Транскрипция: {term.term_transliteration} ({term.term_transliteration_cyr})
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {term.definition_short}
                  </Typography>
                  
                  {term.definition_full && (
                    <Typography variant="body2" color="text.secondary">
                      {term.definition_full}
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Статистика */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Статистика терминов
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Всего: ${terms.length}`} size="small" />
            <Chip label={`Избранных: ${favoriteTerms.size}`} size="small" color="warning" />
            <Chip 
              label={`Категорий: ${new Set(terms.map(t => t.category)).size}`} 
              size="small" 
              color="info" 
            />
            <Chip 
              label={`Изучено: ${Array.from(studyProgress.values()).filter(p => p.is_mastered).length}`} 
              size="small" 
              color="success" 
            />
            <Chip 
              label={`Просмотрено: ${Array.from(studyProgress.values()).reduce((sum, p) => sum + p.times_viewed, 0)}`} 
              size="small" 
              color="primary" 
            />
          </Box>
          
          {/* Прогресс изучения */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Прогресс изучения:
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(Array.from(studyProgress.values()).filter(p => p.is_mastered).length / terms.length) * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Box>

        {/* Кнопка продолжения */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              const masteredCount = Array.from(studyProgress.values()).filter(p => p.is_mastered).length;
              const totalScore = terms.length * 2 + masteredCount * 3; // Бонус за изучение терминов
              onNext(totalScore);
            }}
            size="large"
          >
            Продолжить
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TermsStep;
